import { useGetIndustryCurriculumList } from "@/api/services/main/industry";
import { TIndustryExam, TIndustrySubject } from "@/api/services/main/company/dtos/get-company-list.dto";
import { TOption } from "@/types/common";
import { SELECTABLE_PART_TYPES } from "@/types/enum";
import { useCallback, useMemo } from "react";

// Industry shape consumed by the learner form/detail: name + curriculum data (科目/受験).
export type TLearnerIndustryWithCurriculum = {
  id: number;
  name: string;
  // Total subject count of the industry (from /industries). Drives the 学習アプリを利用する toggle.
  subjectCount?: number | null;
  // Selectable part types of the industry (from /industries).
  partTypes?: string[];
  subjects?: TIndustrySubject[];
  exams?: TIndustryExam[];
  mockExamQuestionCount?: number | null;
};

// 分野の「受験・学習対象のパート」候補を導出する。form / detail / 一括取り込みで共通利用する単一定義。
// 分野自身の partTypes (from /industries) を優先し、無ければ subjects の partType から補完する
// (保存済み/非表示分野で partTypes が欠ける場合のフォールバック)。SELECTABLE_PART_TYPES で選択可能なものだけに絞る。
export const getIndustryPartTypes = (industry?: TLearnerIndustryWithCurriculum | null): string[] => {
  const selectableSet: ReadonlySet<string> = new Set(SELECTABLE_PART_TYPES);
  const source = industry?.partTypes?.length ? industry.partTypes : (industry?.subjects ?? []).map((s) => s.partType);
  return Array.from(new Set(source.filter((pt) => selectableSet.has(pt))));
};

/**
 * Industry options for the learner form, sourced ENTIRELY from the main 分野 list
 * (`useGetIndustryCurriculumList` → main service /industries). BE scopes the list per authenticated
 * user (RSO/company hiddenIndustryIds, hasLearner) and returns per-user counts — the FE does NOT
 * read industries from the company. Curriculum data (subjects/exams/partTypes) comes from each item.
 */
export const useLearnerIndustryOptions = (
  // Industry the learner already has saved — fallback when it's been hidden so the edit/detail
  // screen can still display the name and the saved exam/subjects.
  fallbackIndustry?: (Partial<TLearnerIndustryWithCurriculum> & { id: number; name: string }) | null,
) => {
  const { visibleIndustries, isLoading } = useGetIndustryCurriculumList();

  const industries: TLearnerIndustryWithCurriculum[] = useMemo(
    () =>
      visibleIndustries.map((ind) => ({
        id: ind.id,
        name: ind.name,
        subjectCount: ind.subjectCount != null ? Number(ind.subjectCount) : null,
        partTypes: ind.partTypes ?? [],
        subjects: ind.subjects ?? [],
        exams: (ind.exams ?? []).map((e) => ({
          id: String(e.id),
          periodName: e.periodName,
          status: e.status,
          examMethod: e.examMethod,
          examMonth: e.examMonth ?? null,
        })),
        mockExamQuestionCount: ind.mockExamQuestionCount ?? null,
      })),
    [visibleIndustries],
  );

  const industryOptions: TOption[] = useMemo(() => {
    const opts = industries.map((ind) => ({ label: ind.name, value: ind.id }));
    if (
      fallbackIndustry?.id != null
      && !opts.some((o) => Number(o.value) === Number(fallbackIndustry.id))
    ) {
      opts.push({ label: fallbackIndustry.name, value: fallbackIndustry.id });
    }
    return opts;
  }, [industries, fallbackIndustry]);

  const getIndustryById = useCallback(
    (industryId: number | string | null | undefined): TLearnerIndustryWithCurriculum | undefined => {
      if (!industryId) return undefined;
      const found = industries.find((ind) => String(ind.id) === String(industryId));
      if (found) return found;
      if (fallbackIndustry?.id != null && String(fallbackIndustry.id) === String(industryId)) {
        return fallbackIndustry as TLearnerIndustryWithCurriculum;
      }
      return undefined;
    },
    [industries, fallbackIndustry],
  );

  return { industryOptions, industries, getIndustryById, isLoading };
};
